import type { AdminScope, AdminSectionContribution } from '@os/contracts/web'
import { filterAndSortSections } from './admin-registry-filter'
import type { AdminGateCtx } from './adminGate'
import { passesAdminGate } from './adminGate'
import platformAdminModule from '../modules/platform/platformAdminModule'
import serverAdminModule from '../modules/platform/serverAdminModule'
import { buildPlatformModuleDefaults, buildServerModuleDefaults } from './moduleAdminDefaults'
import { isModuleEnabled } from '@os/contracts/shared'

// In-core admin providers — always available, no async module load needed.
// Panels are still lazy; only the lightweight manifests are statically imported.
//
// Sibling modules will ALSO contribute admin sections via the dynamic web
// module registry, but that path is wired in a later extraction phase (the one
// that actually invokes loadAllModules()). We deliberately do NOT import the
// module registry here yet: it is dormant in phase 0a (nothing registers, and
// loadAllModules is never called), and importing it would pull its dormant
// `import('@os/<module>/web')` calls into the bundle graph for zero benefit —
// which also trips vite's dep-scan because some siblings only export `./web/*`,
// not a bare `./web`. Re-add the dynamic merge (with id de-dup, static wins)
// when loadAllModules is wired.
const STATIC_PROVIDERS = [platformAdminModule, serverAdminModule]

export function collectAdminSections(): AdminSectionContribution[] {
  return STATIC_PROVIDERS.flatMap((m) => m.adminSections ?? [])
}

export function getAdminSections(scope: AdminScope, ctx: AdminGateCtx): AdminSectionContribution[] {
  return filterAndSortSections(collectAdminSections(), scope, ctx)
}

export interface GroupedAdminSections {
  modules: AdminSectionContribution[]
  platform: AdminSectionContribution[]
}

export function getGroupedPlatformSections(ctx: AdminGateCtx): GroupedAdminSections {
  const contributions = collectAdminSections().filter((s) => s.scope === 'platform')
  const overrides = new Map(contributions.filter((s) => s.moduleKey).map((s) => [s.moduleKey!, s]))
  const platformContribs = contributions.filter((s) => !s.moduleKey)

  const modules = buildPlatformModuleDefaults()
    .map((def) => overrides.get(def.moduleKey!) ?? def) // override wins, keeps slot/order
    .filter((s) => passesAdminGate(s.gate, ctx))

  const platform = platformContribs
    .filter((s) => passesAdminGate(s.gate, ctx))
    .sort((a, b) => a.order - b.order)

  return { modules, platform }
}

export function getGroupedServerSections(ctx: AdminGateCtx): GroupedAdminSections {
  const contributions = collectAdminSections().filter((s) => s.scope === 'server')
  const overrides = new Map(contributions.filter((s) => s.moduleKey).map((s) => [s.moduleKey!, s]))
  const platformContribs = contributions.filter((s) => !s.moduleKey)

  const modules = buildServerModuleDefaults()
    .map((def) => overrides.get(def.moduleKey!) ?? def)
    .filter((s) => isModuleEnabled(ctx.enabledModules as Parameters<typeof isModuleEnabled>[0], s.moduleKey!))
    .filter((s) => passesAdminGate(s.gate, ctx))

  const platform = platformContribs
    .filter((s) => passesAdminGate(s.gate, ctx))
    .sort((a, b) => a.order - b.order)

  return { modules, platform }
}
